import { useEffect, useState } from "react";
import { Occurrence, OccurrenceDispatch, OccurrenceReview, OccurrenceWithTimeSpent } from "../models/occurrence";
import { fetchOccurenceList, fetchOccurenceListWithTimeSpent, fetchOccurrenceStepAvailable } from "../api/fetchOccurence";
import { getOccurrenceStepKeyOfValue, OccurrenceStepEnum, OccurrenceStepLabelEnum } from "../enums/occurrenceStep";
import { cancelOccurrenceDispatch, changeOccurrenceStep, createOccurrence, createOccurrenceDispatch, finishOccurrence, getOccurrenceReview, reviewOccurrenceStep, updateOccurrence } from "../api/occurrence";
import { getAssetIdByChassis, getChassisListFromAssetIds } from "../utils/assetUtils";
import useAssets from "./useAssets";
import { signRevision } from "../api/signedRevision";
import { useSelector } from "react-redux";
import { getUserProfile } from "../configuration/login/loginSlice";
import { fetchComplementaryAssetsDetails } from "../api/fetchAssets";
import { fetchUser } from "../api/fetchUser";
import { sendBotMessage } from "../services/amityService";
import { getFinalizedFilterValue } from "../utils/kanbanUtils";
import { Step } from "../models/step";
import { fetchSteps } from "../api/step";

function useOccurrence(userRole: 'TOWER' | 'CONSULTANT' | 'MANAGER' | 'PARTNERSHIP', finalizedFilter?: boolean, assetIds?: string[], chassisList?: string[]) {

    const userProfile = useSelector(getUserProfile);
    const limit = 50;

    /**
     * Filtros
    */
    const [criticalityFilter, setCriticalityFilter] = useState<string>("");
    const [typeFilter, setTypeFilter] = useState<string[]>([]);
    const [isAlertFilter, setIsAlertFilter] = useState<string>("");
    const [chassisFilter, setChassisFilter] = useState<string>("");
    const [searchFieldName, setSearchFieldName] = useState<string>("CHASSIS");
    const [searchFieldValue, setSearchFieldValue] = useState<string>("");
    const [chassisListFilter, setChassisListFilter] = useState<string[]>([]);
    const [chassisListFilterByStep, setChassisListFilterByStep] = useState<string[]>([]);
    const [startDateFilter, setStartDateFilter] = useState<{ start?: string, end?: string } | null>(null);
    const [updateDateFilter, setUpdateDateFilter] = useState<{ start?: string, end?: string } | null>(null);
    const [finishedFilter, setFinishedFilter] = useState<string[]>(['false']);
    const [dealershipFilter, setDealershipFilter] = useState<string[]>([]);
    const [regionalFilter, setRegionalFilter] = useState<string[]>([]);
    const [cellFilter, setCellFilter] = useState<string[]>([]);
    const [stepFilter, setStepFilter] = useState<OccurrenceStepEnum | null>(null);

    /**
     * Estado
    */
    const [viewType, setViewType] = useState<"card" | "list">("card");
    const [firstLoad, setFirstLoad] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingMoreData, setLoadingMoreData] = useState<boolean>(false);
    const [page, setPage] = useState<number>(0);

    /**
     * Listas
    */
    const { allAssets } = useAssets();
    const [occurrenceList, setOccurrenceList] = useState<Occurrence[]>([]);
    const [totalOccurrences, setTotalOccurrences] = useState<number>(0);
    const [occurrenceWithTimeSpentList, setOccurrenceWithTimeSpentList] = useState<OccurrenceWithTimeSpent[]>([]);
    const [finalizedOccurrenceWithTimeSpentList, setFinalizedOccurrenceWithTimeSpentList] = useState<OccurrenceWithTimeSpent[]>([]);
    const [uniqueAssetIds, setUniqueAssetIds] = useState<string[]>([]);
    const [occurrenceStepAvailable, setOccurrenceStepAvailable] = useState<string[]>([]);
    const [steps, setSteps] = useState<Step[]>([]);

    const proceedWithFilters = () => ((assetIds && assetIds.length > 0) || userRole === 'PARTNERSHIP');

    useEffect(() => {
        const fetchStepsAvailable = async () => {
            const steps = await fetchOccurrenceStepAvailable();
            setOccurrenceStepAvailable(steps.map(step => getOccurrenceStepKeyOfValue(step)));

            const stepsWithElapsedHours = await fetchSteps();
            setSteps(stepsWithElapsedHours);
        }

        fetchStepsAvailable();
    }, []);

    const getChassisList = (assetIds: string[]) => {
        return getChassisListFromAssetIds(allAssets, assetIds.filter(Boolean)).filter(Boolean);
    }

    useEffect(() => {
        setLoading(true);

        const localUniqueAssetIds = Array.from(new Set(assetIds));
        setUniqueAssetIds(prev => prev = localUniqueAssetIds);

        if (
            assetIds && assetIds.length > 0 &&
            userRole !== 'PARTNERSHIP'
        ) setChassisListFilter(getChassisList(localUniqueAssetIds));

        if (
            assetIds && assetIds.length === 0 && !firstLoad
        ) {
            setOccurrenceList([]);
            setPage(0);
            setLoading(false);
            setLoadingMoreData(false);
        }

        if (
            (assetIds && assetIds.length > 0) ||
            (userRole === 'PARTNERSHIP' || userRole === 'CONSULTANT')
        ) {
            getOccurrenceList({ localPage: 0 });
        }

        if (assetIds && assetIds.length > 0 && firstLoad) {
            setFirstLoad(false);
        }
    }, [assetIds]);

    useEffect(() => {
        if (chassisList && chassisList.length > 0) {
            setChassisListFilter(chassisList);
            if (proceedWithFilters()) {
                getOccurrenceList({ localPage: 0, localChassisListFilter: chassisList });
            }
        } else if (chassisList && chassisList.length === 0) {
            setChassisListFilter([]);
        }
    }, [chassisList]);

    const getChassisListFilter = (chassisFilter: string) => {
        return chassisFilter === '' && uniqueAssetIds && uniqueAssetIds.length > 0 ?
            getChassisList(uniqueAssetIds) :
            chassisFilter ? [chassisFilter] : chassisListFilter ?? undefined
    }

    useEffect(() => {
        if ((chassisListFilter && chassisListFilter.length > 0) || proceedWithFilters()) {
            getOccurrenceList({
                localPage: 0,
                localChassisListFilter: chassisListFilter
            });
        }
    }, [chassisListFilter]);

    useEffect(() => {
        if (proceedWithFilters()) {
            getOccurrenceList({localPage: 0});
        }
    }, [chassisListFilterByStep]);

    useEffect(() => {
        if (chassisFilter || chassisFilter === "" && proceedWithFilters()) {
            getOccurrenceList({
                localPage: 0,
                localChassisListFilter: getChassisListFilter(chassisFilter)
            });
        }
    }, [chassisFilter, searchFieldValue]);

    useEffect(() => {
        if (searchFieldValue && proceedWithFilters()) {
            getOccurrenceList({
                localPage: 0,
                localChassisListFilter: undefined
            });
        }
    }, [searchFieldValue]);

    useEffect(() => {
        if (criticalityFilter && proceedWithFilters()) {
            getOccurrenceList({
                localPage: 0,
                localChassisListFilter: getChassisListFilter(chassisFilter)
            });
        }
    }, [criticalityFilter]);

    useEffect(() => {
        if (typeFilter && proceedWithFilters()) {
            getOccurrenceList({
                localPage: 0,
                localChassisListFilter: getChassisListFilter(chassisFilter)
            });
            // getQuantityAnalytics({ localChassisListFilter: getChassisListFilter(chassisFilter) });
        }
    }, [typeFilter]);

    useEffect(() => {
        if (isAlertFilter && assetIds && assetIds.length > 0) {
            getOccurrenceList({
                localPage: 0,
                localChassisListFilter: getChassisListFilter(chassisFilter)
            });
        }
    }, [isAlertFilter]);

    useEffect(() => {
        if (finishedFilter.length >= 0 && proceedWithFilters()) {
            getOccurrenceList({
                localPage: 0,
                localChassisListFilter: getChassisListFilter(chassisFilter)
            });
        }
    }, [finishedFilter]);

    useEffect(() => {
        if (regionalFilter && proceedWithFilters()) {
            getOccurrenceList({ localPage: 0, localChassisListFilter: getChassisListFilter(chassisFilter) });
        }
    }, [regionalFilter]);

    useEffect(() => {
        if (cellFilter && proceedWithFilters()) {
            getOccurrenceList({ localPage: 0, localChassisListFilter: getChassisListFilter(chassisFilter) });
        }
    }, [cellFilter]);

    useEffect(() => {
        if (dealershipFilter.length >= 0 && proceedWithFilters()) {
            getOccurrenceList({
                localPage: 0,
                localChassisListFilter: getChassisListFilter(chassisFilter)
            });
        }
    }, [dealershipFilter]);

    useEffect(() => {
        if (
            startDateFilter && (
                proceedWithFilters() &&
                startDateFilter?.start && startDateFilter?.end ||
                !startDateFilter?.start && !startDateFilter?.end)
        ) {
            getOccurrenceList({
                localPage: 0,
                localChassisListFilter: getChassisListFilter(chassisFilter)
            });
        }
    }, [startDateFilter]);

    useEffect(() => {
        if (
            updateDateFilter && (
                proceedWithFilters() &&
                updateDateFilter?.start && updateDateFilter?.end ||
                !updateDateFilter?.start && !updateDateFilter?.end)
        ) {
            getOccurrenceList({
                localPage: 0,
                localChassisListFilter: getChassisListFilter(chassisFilter)
            });
        }
    }, [updateDateFilter]);

    useEffect(() => {
        if (assetIds && assetIds.length > 0) {
            getOccurrenceList({
                localPage: 0,
                localChassisListFilter: getChassisListFilter(chassisFilter)
            });
        }
    }, [stepFilter]);

    const searchByChassis = (searchTerm: string, fieldName?: string) => {
        setSearchFieldName(fieldName ?? 'CHASSIS');
        setSearchFieldValue(searchTerm);
        setChassisFilter(searchTerm);
    };

    // ---------- Helpers p/ exportação (não afetam a UI) ----------
    const buildFilters = (override?: { chassisListFilter?: string[] }) => ({
        criticalityFilter:
            criticalityFilter === "undefined" ? undefined : criticalityFilter,
        typeFilter: typeFilter.length > 0 ? typeFilter : undefined,
        chassisListFilter:
            override?.chassisListFilter ??
            (chassisFilter === "" && uniqueAssetIds && uniqueAssetIds.length > 0
                ? getChassisList(uniqueAssetIds)
                : chassisFilter
                    ? [chassisFilter]
                    : chassisListFilter ?? undefined),
        startDateFilter: startDateFilter?.start,
        endDateFilter: startDateFilter?.end,
        updateDateFilter: updateDateFilter?.start,
        updateDateFilterEnd: updateDateFilter?.end,
        dealershipFilter: dealershipFilter,
        regionalFilter: regionalFilter.length > 0 ? regionalFilter : undefined,
        cellFilter: cellFilter.length > 0 ? cellFilter : undefined,
    });

    /** Busca página “crua” para o exportador */
    const fetchOccurrencePage = async ({
        page,
        size = limit,
    }: {
        page: number;
        size?: number;
    }): Promise<Occurrence[]> => {
        const data = await fetchOccurenceList(
            buildFilters(),
            userRole,
            page,
            size,
            getFinalizedFilterValue(finishedFilter, finalizedFilter)
        );
        return data.occurrences;
    };

    /** Reseta paginação da UI quando necessário (ex.: antes de novo carregamento total) */
    const resetPaging = () => {
        setPage(0);
    };

    const getOccurrenceList = async ({ localPage, localChassisListFilter }: { localPage?: number, localChassisListFilter?: string[] }) => {
        if (!chassisFilter &&
            (!localChassisListFilter || localChassisListFilter.length === 0) &&
            userRole !== 'PARTNERSHIP' &&
            (!uniqueAssetIds || uniqueAssetIds.length === 0)) {
            return;
        }

        if (localPage === 0 || page === 0) setLoading(true);
        else setLoadingMoreData(true);

        // Se o usuário for consultante e a quantidade de chassis for igual a quantidade total de assets na conta, o chassisListFilter deve ser vazio
        if (userRole === 'CONSULTANT' && ((localChassisListFilter && localChassisListFilter.length === allAssets.length) || !localChassisListFilter)) {
            localChassisListFilter = [];
        }

        const filters = {
            criticalityFilter: criticalityFilter === "undefined" ? undefined : criticalityFilter,
            typeFilter: typeFilter.length > 0 ? typeFilter : undefined,
            isAlertFilter: isAlertFilter === "undefined" ? undefined : isAlertFilter,
            chassisFilter: !searchFieldValue ? (chassisFilter || undefined) : undefined,
            chassisListFilter: !searchFieldValue ? (localChassisListFilter ?? getChassisListFilter(chassisFilter)) : undefined,
            chassisListFilterByStep: chassisListFilterByStep,
            startDateFilter: startDateFilter?.start,
            endDateFilter: startDateFilter?.end,
            updateDateFilter: updateDateFilter?.start,
            updateDateFilterEnd: updateDateFilter?.end,
            dealershipFilter: dealershipFilter,
            regionalFilter: regionalFilter.length > 0 ? regionalFilter : undefined,
            cellFilter: cellFilter.length > 0 ? cellFilter : undefined,
            stepFilter,
            searchFieldName: searchFieldValue ? searchFieldName : undefined,
            searchFieldValue: searchFieldValue || undefined,
        }

        const [data, allOccurrenceWithTimeSpent] = await Promise.all([
            fetchOccurenceList(filters, userRole, localPage ?? page, limit, getFinalizedFilterValue(finishedFilter, finalizedFilter)),
            fetchOccurenceListWithTimeSpent(filters, userRole, getFinalizedFilterValue(finishedFilter, finalizedFilter) ?? undefined)
        ]);
        const finalizedFilterValue = getFinalizedFilterValue(finishedFilter, finalizedFilter);
        const occurrenceWithTimeSpentList = [
            ...(!finalizedFilterValue ? allOccurrenceWithTimeSpent.current : []),
            ...((!finalizedFilterValue || finalizedFilterValue === true) ? allOccurrenceWithTimeSpent.finalized : []),
        ];

        setOccurrenceList([
            ...(localPage === 0 || page === 0 ? [] : occurrenceList),
            ...data.occurrences.map(occurrence => {
                const occurrenceWithTimeSpent = occurrenceWithTimeSpentList.find(el => el.occurrenceId === occurrence.id);
                return {
                    ...occurrence,
                    timeSpent: occurrenceWithTimeSpent?.timeSpent ?? 0
                }
            })
        ]);
        setTotalOccurrences(data.total);
        setLoading(false);
        setLoadingMoreData(false);
        
        setOccurrenceWithTimeSpentList(occurrenceWithTimeSpentList);
        setFinalizedOccurrenceWithTimeSpentList(allOccurrenceWithTimeSpent.finalized);
        setPage(localPage || localPage === 0 ? localPage + 1 : page + 1);

        addAccountNameAndSaleDate([...(localPage === 0 || page === 0 ? [] : occurrenceList), ...data.occurrences]);
    }

    const refreshCurrentData = async () => {
        const filters = {
            criticalityFilter: criticalityFilter === "undefined" ? undefined : criticalityFilter,
            typeFilter: typeFilter.length > 0 ? typeFilter : undefined,
            chassisListFilter: getChassisListFilter(chassisFilter),
            startDateFilter: startDateFilter?.start,
            endDateFilter: startDateFilter?.end,
            updateDateFilter: updateDateFilter?.start,
            updateDateFilterEnd: updateDateFilter?.end,
            dealershipFilter: dealershipFilter,
            regionalFilter: regionalFilter.length > 0 ? regionalFilter : undefined,
            cellFilter: cellFilter.length > 0 ? cellFilter : undefined,
            stepFilter,
        }

        try {
            const data = await fetchOccurenceList(filters, userRole, 0, limit * page, getFinalizedFilterValue(finishedFilter, finalizedFilter));

            setOccurrenceList(data.occurrences);
            setTotalOccurrences(data.total);

            addAccountNameAndSaleDate(data.occurrences);
        } catch (error) {
            console.error("Erro ao atualizar dados:", error);
        }
    }

    const reloadOccurrenceList = async (occurrenceIdToRemove?: string, occurrenceIdToAdd?: string) => {
        setLoading(true);
        const fetchData = async (page: number) => {
            const filters = {
                criticalityFilter: criticalityFilter === "undefined" ? undefined : criticalityFilter,
                chassisFilter,
                chassisListFilter: chassisFilter === '' && uniqueAssetIds ?
                    getChassisList(uniqueAssetIds) :
                    chassisFilter ?
                        [chassisFilter] :
                        undefined,
                startDateFilter: startDateFilter?.start,
                endDateFilter: startDateFilter?.end,
                updateDateFilter: updateDateFilter?.start,
                updateDateFilterEnd: updateDateFilter?.end,
                regionalFilter: regionalFilter.length > 0 ? regionalFilter : undefined,
                cellFilter: cellFilter.length > 0 ? cellFilter : undefined,
                stepFilter: stepFilter ?? undefined,
            }
            const data = await fetchOccurenceList(filters, userRole, page, limit, getFinalizedFilterValue(finishedFilter, finalizedFilter));
            const allOccurrenceWithTimeSpent = await fetchOccurenceListWithTimeSpent(filters, userRole, getFinalizedFilterValue(finishedFilter, finalizedFilter) ?? undefined);
            const finalizedFilterValue = getFinalizedFilterValue(finishedFilter, finalizedFilter);
            const occurrenceWithTimeSpentList = [
                ...(!finalizedFilterValue ? allOccurrenceWithTimeSpent.current : []),
                ...((!finalizedFilterValue || finalizedFilterValue === true) ? allOccurrenceWithTimeSpent.finalized : []),
            ];

            const totalInService = !stepFilter ?
                occurrenceWithTimeSpentList.reduce((acc, curr) => acc + curr.timeSpent, 0) :
                occurrenceWithTimeSpentList.reduce((acc, curr) => acc + (curr.steps[stepFilter]?.total ?? 0), 0);

            setOccurrenceWithTimeSpentList(occurrenceWithTimeSpentList);
            setFinalizedOccurrenceWithTimeSpentList(allOccurrenceWithTimeSpent.finalized);

            return data.occurrences;
        }

        const updatedOccurrenceList: Occurrence[] = [];
        for (let i = 0; i < page; i++) {
            const data = await fetchData(i);
            updatedOccurrenceList.push(...data);
        }

        let newOccurrenceList = [...occurrenceList];

        const occurrenceToAdd = occurrenceIdToAdd ? updatedOccurrenceList.find(occurrenceEl => occurrenceEl.id === occurrenceIdToAdd) : null;
        if (occurrenceToAdd) {
            newOccurrenceList.push(occurrenceToAdd);
        }

        setOccurrenceList([
            ...newOccurrenceList
                .filter(occurrenceEl => occurrenceEl.id !== occurrenceIdToRemove)
                .map(occurrenceEl => updatedOccurrenceList.find(updatedOccurrenceEl => updatedOccurrenceEl.id === occurrenceEl.id) ?? occurrenceEl),
        ]);

        setOccurrenceWithTimeSpentList(occurrenceWithTimeSpentList);
        setFinalizedOccurrenceWithTimeSpentList(finalizedOccurrenceWithTimeSpentList);

        setLoading(false);

        addAccountNameAndSaleDate([
            ...newOccurrenceList
                .filter(occurrenceEl => occurrenceEl.id !== occurrenceIdToRemove)
                .map(occurrenceEl => updatedOccurrenceList.find(updatedOccurrenceEl => updatedOccurrenceEl.id === occurrenceEl.id) ?? occurrenceEl),
        ]);
    }

    const addAccountNameAndSaleDate = async (data: Occurrence[]) => {
        const assetIds = data.map(occurrence => getAssetIdByChassis(allAssets, occurrence.vehicleChassis)).filter(el => el !== undefined && el !== null);
        const complementaryAssetsDetails = await fetchComplementaryAssetsDetails(assetIds);

        setOccurrenceList(prev => prev.map(occurrence => {
            const vehicleData = complementaryAssetsDetails.find(el => el.id === getAssetIdByChassis(allAssets, occurrence.vehicleChassis));
            return {
                ...occurrence,
                accountName: vehicleData?.accountName ?? '-',
                vehicleSaleDate: vehicleData?.saleDate ? new Date(vehicleData.saleDate) : undefined,
            };
        }));
    }

    const changeStep = async (occurrence: Occurrence, fromStepId: number, to: OccurrenceStepEnum): Promise<Occurrence | null> => {
        const toStepEnum = Object.keys(OccurrenceStepEnum).find(key => OccurrenceStepEnum[key as keyof typeof OccurrenceStepEnum] === to) as keyof typeof OccurrenceStepEnum;

        try {
            const updatedOccurrence = await changeOccurrenceStep(occurrence, fromStepId, to);
            if (!updatedOccurrence) return null;

            setOccurrenceList([
                ...occurrenceList
                    .map(occurrenceEl => occurrenceEl.id === occurrence.id ? updatedOccurrence : occurrenceEl),
            ]);

            if (occurrence.createdByUserProfileId) {
                try {
                    sendBotMessage(
                        `O chamado do veículo ${occurrence.vehicleName ?? ''} com chassi ${occurrence.vehicleChassis ?? ''} está na etapa: ${OccurrenceStepLabelEnum[toStepEnum] ?? to}`,
                        occurrence.createdByUserProfileId
                    );
                } catch (error) {
                    console.error("Erro ao enviar mensagem:", error);
                }
            }

            return updatedOccurrence;
        } catch (error: any) {
            console.error("Erro ao atualizar:", error);
            throw error;
        }
    }

    const isValidStep = (requiredFields: string[], occurrence: Occurrence): boolean => {
        if (!occurrence) return false;

        // Validate failures
        if (occurrence.failures && occurrence.failures.length > 0) {
            const missingFailures = occurrence.failures.filter(failure =>
                failure.failureType === undefined || failure.failureType === null || failure.failureType === "undefined"
            );
            if (missingFailures.length > 0) return false;
        }

        // Validate part orders
        if (occurrence.partOrders && occurrence.partOrders.length > 0) {
            const missingPartOrders = occurrence.partOrders.filter(partOrder =>
                partOrder.number === undefined || partOrder.number === null || partOrder.number === "" ||
                partOrder.date === undefined || partOrder.date === null
            );
            if (missingPartOrders.length > 0) return false;
        }

        // Validate parts order list
        if (occurrence.partOrderParts && occurrence.partOrderParts.length > 0) {
            const missingParts = occurrence.partOrderParts.filter(part => 
                part.partNumber === undefined || part.partNumber === null || part.partNumber === "" ||
                part.quantity === undefined || part.quantity === null || part.quantity === ""
            );
            if (missingParts.length > 0) return false;
        }

        return requiredFields.every(field => {
            // Validate array attributes
            if (field === 'failures') return occurrence.failures && occurrence.failures?.length > 0;
            if (field === 'modules') return occurrence.modules && occurrence.modules?.length > 0;
            if (field === 'partOrders') return occurrence.partOrders && occurrence.partOrders?.length > 0;
            if (field === 'partOrderParts') return occurrence.partOrderParts && occurrence.partOrderParts?.length > 0;
            if (field === 'partOrderDeliveryDates') return occurrence.partOrderDeliveryDates && occurrence.partOrderDeliveryDates?.length > 0;

            if (field === 'vehicleModel') {
                const hasModelId = occurrence.vehicleModel !== undefined && occurrence.vehicleModel !== null && occurrence.vehicleModel !== "";
                const hasModelName = occurrence.model !== undefined && occurrence.model !== null && occurrence.model !== "";
                return hasModelId || hasModelName;
            }

            const value = occurrence[field as keyof Occurrence];
            return value !== undefined && value !== null && value !== "";
        });
    }

    const getNotFilledRequiredFields = (requiredFields: string[], occurrence: Occurrence): string[] => {
        let missingFields: string[] = [];

        // Validate failures list
        if (occurrence.failures && occurrence.failures.length > 0) {
            const missingFailures = occurrence.failures.filter(failure =>
                failure.failureType === undefined || failure.failureType === null || failure.failureType === "undefined"
            );
            if (missingFailures.length > 0) missingFields.push("failures");
        }

        // Validate part orders list
        if (occurrence.partOrders && occurrence.partOrders.length > 0) {
            const missingPartOrders = occurrence.partOrders.filter(partOrder =>
                partOrder.number === undefined || partOrder.number === null || partOrder.number === "" ||
                partOrder.date === undefined || partOrder.date === null
            );
            if (missingPartOrders.length > 0) missingFields.push("partOrders");
        }

        // Validate parts order list
        if (occurrence.partOrderParts && occurrence.partOrderParts.length > 0) {
            const missingParts = occurrence.partOrderParts.filter(part => 
                part.partNumber === undefined || part.partNumber === null || part.partNumber === "" ||
                part.quantity === undefined || part.quantity === null || part.quantity === ""
            );
            if (missingParts.length > 0) missingFields.push("partOrderParts");
        }

        missingFields.push(...requiredFields.filter(field => {
            // Validate array attributes
            if (field === 'failures') return !(occurrence.failures && occurrence.failures?.length > 0);
            if (field === 'modules') return !(occurrence.modules && occurrence.modules?.length > 0);
            if (field === 'partOrders') return !(occurrence.partOrders && occurrence.partOrders?.length > 0);
            if (field === 'partOrderParts') return !(occurrence.partOrderParts && occurrence.partOrderParts?.length > 0);
            if (field === 'partOrderDeliveryDates') return !(occurrence.partOrderDeliveryDates && occurrence.partOrderDeliveryDates?.length > 0);

            // Validação especial para modelo do veículo: aceita OU vehicleModel (ID) OU model (nome)
            if (field === 'vehicleModel') {
                const hasModelId = occurrence.vehicleModel !== undefined && occurrence.vehicleModel !== null && occurrence.vehicleModel !== "";
                const hasModelName = occurrence.model !== undefined && occurrence.model !== null && occurrence.model !== "";
                return !(hasModelId || hasModelName);
            }

            const value = occurrence[field as keyof Occurrence];
            return value === undefined || value === null || value === "";
        }));

        return missingFields;
    }

    const addDispatch = async (occurrence: Occurrence, dispatch: OccurrenceDispatch): Promise<{ wasCreated: boolean, error?: string, dispatch?: OccurrenceDispatch }> => {
        setLoading(true);
        try {
            const dispatchCreated = await createOccurrenceDispatch(occurrence.id, dispatch);
            if (dispatchCreated) {
                return { wasCreated: true, dispatch: dispatchCreated };
            } else {
                setLoading(false);
                return { wasCreated: false, error: "Erro ao criar acionamento" };
            }
        } catch (error: any) {
            console.error("Erro ao criar acionamento:", error);
            setLoading(false);
            return { wasCreated: false, error: error };
        }
    }

    const cancelDispatch = async (occurrence: Occurrence, dispatch: OccurrenceDispatch, reason: string, description: string): Promise<{ wasCanceled: boolean, error?: string, dispatch?: OccurrenceDispatch }> => {
        setLoading(true);
        try {
            const dispatchCanceled = await cancelOccurrenceDispatch(occurrence.id, dispatch.id!, { reason, description });
            if (dispatchCanceled) {
                return { wasCanceled: true, dispatch: dispatchCanceled };
            } else {
                setLoading(false);
                return { wasCanceled: false, error: "Erro ao cancelar acionamento" };
            }
        } catch (error: any) {
            console.error("Erro ao cancelar acionamento:", error);
            setLoading(false);
            return { wasCanceled: false, error: error };
        }
    }

    const create = async (occurrence: Occurrence): Promise<{ wasCreated: boolean, error?: string }> => {
        setLoading(true);
        try {
            occurrence.steps = occurrence.steps?.map(step => {
                if (step.step === OccurrenceStepEnum.CALL) {
                    return {
                        ...step,
                        dtStart: new Date(),
                    };
                }
                return step;
            });

            let user;
            const userId = userProfile?.sub?.split(":")[1];
            if (userId) user = await fetchUser(userId);

            occurrence.createdBy = user?.name;

            const { data, error } = await createOccurrence(occurrence);
            if (error) throw error;
            await reloadOccurrenceList(undefined, occurrence.id);
            setLoading(false);
            return { wasCreated: !!data };
        } catch (error: any) {
            console.error("Erro ao criar:", error);
            setLoading(false);
            return { wasCreated: false, error: error };
        }
    }

    const save = async (occurrence: Occurrence): Promise<boolean> => {
        try {
            const occurrenceUpdated = await updateOccurrence(occurrence);
            if (!occurrenceUpdated) return false;

            setOccurrenceList([
                ...occurrenceList
                    .map(occurrenceEl => occurrenceEl.id === occurrence.id ? occurrenceUpdated : occurrenceEl),
            ]);
            return !!occurrenceUpdated;
        } catch (error: any) {
            console.error("Erro ao salvar:", error);
            return false;
        }
    }

    const finish = async (
        occurrence: Occurrence, 
        finalizationReasonType?: string, 
        finalizationReasonDescription?: string,
        fromStepId?: number,
    ): Promise<boolean> => {
        try {
            await finishOccurrence(occurrence, finalizationReasonType, finalizationReasonDescription, fromStepId);

            // Blockchain - Sign occurrence
            await signRevision({
                id: occurrence.id,
                reviewDate: new Date(),
                reviewName: occurrence.scheduleNumber ?? '',
                chassis: occurrence.vehicleChassis ?? '',
                dealerId: occurrence.dealershipDn ?? '',
                dealerName: occurrence.dealershipName ?? '',
                odometer: occurrence.vehicleOdometer ?? 0,
                accountId: userProfile?.account ?? ''
            });

            await reloadOccurrenceList(occurrence.id);
            return true;
        } catch (error: any) {
            console.error("Erro ao concluir ocorrência:", error);
            throw error;
        }
    }

    /**
     * Review
     */
    const [review, setReview] = useState<OccurrenceReview[]>([]);

    const getReview = async (occurrenceId: string) => {
        const response = await getOccurrenceReview(occurrenceId);
        setReview(response);
    }

    const reviewStep = async (occurrenceId: string, step: OccurrenceStepEnum, rate: number, comment?: string) => {
        await reviewOccurrenceStep(occurrenceId, { step, rate, comment });
        await getReview(occurrenceId);
    }

    return {
        viewType,
        setViewType,
        searchByChassis,
        setChassisListFilter,
        setCriticalityFilter,
        criticalityFilter,
        typeFilter,
        setTypeFilter,
        setStartDateFilter,
        startDateFilter,
        setUpdateDateFilter,
        setIsAlertFilter,
        updateDateFilter,
        finishedFilter,
        setFinishedFilter,
        setDealershipFilter,
        dealershipFilter,
        regionalFilter,
        setRegionalFilter,
        cellFilter,
        setCellFilter,
        setStepFilter,
        chassisListFilter,
        occurrenceList,
        occurrenceWithTimeSpentList,
        finalizedOccurrenceWithTimeSpentList,
        firstLoad,
        loading,
        setLoading,
        isValidStep,
        getNotFilledRequiredFields,
        addDispatch,
        cancelDispatch,
        create,
        save,
        changeStep,
        finish,
        loadingMoreData,
        getOccurrenceList,
        refreshCurrentData,
        review,
        getReview,
        reviewStep,
        occurrenceStepAvailable,
        fetchOccurrencePage,
        resetPaging,
        stepFilter,
        totalOccurrences,
        steps,
        chassisListFilterByStep,
        setChassisListFilterByStep,
    }
}

export default useOccurrence; 